import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { useQueryClient } from '@tanstack/react-query';
import QRCode from 'qrcode';
import gsap from 'gsap';
import { apiFetch, ApiError } from '../../lib/api.js';
import './setup2fa.css';

export function Enroll2FAPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [otpauthUri, setOtpauthUri] = useState('');
  const [code, setCode] = useState('');
  const [backupCodes, setBackupCodes] = useState<string[] | null>(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const started = useRef(false);

  useEffect(() => {
    if (started.current) return;
    started.current = true;
    apiFetch<{ otpauthUri: string }>('/auth/2fa/enroll', { method: 'POST' })
      .then((res) => setOtpauthUri(res.otpauthUri))
      .catch((err) => setError(err instanceof ApiError ? err.message : 'enrollment failed'));
  }, []);

  useEffect(() => {
    if (otpauthUri && canvasRef.current) {
      void QRCode.toCanvas(canvasRef.current, otpauthUri, {
        width: 200,
        margin: 1,
        color: { dark: '#0c1117', light: '#f5f2ea' },
      });
      gsap.fromTo('.enroll-card', { y: 12, opacity: 0 }, { y: 0, opacity: 1, duration: 0.4, ease: 'power3.out' });
    }
  }, [otpauthUri]);

  const confirm = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError('');
    try {
      const res = await apiFetch<{ ok: true; backupCodes: string[] }>('/auth/2fa/confirm', {
        method: 'POST',
        body: { code: code.trim() },
      });
      setBackupCodes(res.backupCodes);
      await queryClient.invalidateQueries({ queryKey: ['auth'] });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'invalid code');
    } finally {
      setBusy(false);
    }
  };

  const downloadCodes = () => {
    if (!backupCodes) return;
    const body = [
      'SherpaDeploy — 2FA Backup Codes',
      'Each code can be used once in place of an authenticator code.',
      '',
      ...backupCodes,
    ].join('\r\n');
    const blob = new Blob([body], { type: 'text/plain' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = 'sherpadeploy-2fa-backup-codes.txt';
    a.click();
    URL.revokeObjectURL(a.href);
  };

  if (backupCodes) {
    return (
      <div className="enroll-root">
        <div className="card enroll-card">
          <div className="enroll-eyebrow">Account Security</div>
          <h1>Backup codes</h1>
          <p className="enroll-sub">
            Store these somewhere safe — they are shown <strong>only once</strong>. Each works a single time
            if you lose your authenticator.
          </p>
          <ul className="mono enroll-codes">
            {backupCodes.map((c) => (
              <li key={c}>{c}</li>
            ))}
          </ul>
          <div className="enroll-actions">
            <button className="btn btn-ghost" onClick={downloadCodes}>
              Download .txt
            </button>
            <button className="btn btn-amber" onClick={() => navigate('/')}>
              Continue to dashboard
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="enroll-root">
      <div className="card enroll-card">
        <div className="enroll-eyebrow">Account Security</div>
        <h1>Set up two-factor authentication</h1>
        <p className="enroll-sub">
          Scan with your authenticator app (1Password, Google Authenticator, Authy…), then enter the
          six-digit code to confirm.
        </p>
        {error && !otpauthUri ? <p className="error-text">{error}</p> : null}
        <div className="enroll-qr">
          <canvas ref={canvasRef} />
        </div>
        <form onSubmit={confirm} className="enroll-form">
          <div className="field">
            <label htmlFor="code">Code</label>
            <input
              id="code"
              className="mono"
              inputMode="numeric"
              autoComplete="one-time-code"
              placeholder="000000"
              maxLength={6}
              value={code}
              onChange={(e) => setCode(e.target.value)}
              required
            />
          </div>
          <p className="error-text" role="alert">{otpauthUri ? error : ''}</p>
          <button className="btn btn-amber" disabled={busy || code.trim().length !== 6}>
            {busy ? 'Confirming…' : 'Enable 2FA'}
          </button>
        </form>
      </div>
    </div>
  );
}
